import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, Download, FileText, Loader2, RefreshCw, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';
import { listMinorReleases, decideMinorRelease } from '../services/minorReleaseApi';
import { downloadMinorReleasePdf } from '../pages/minorReleasePdf';
import {
  accessScopeLabel,
  minorReleaseStatusLabel,
  type MinorRelease,
} from '../pages/minorReleaseDocument';

/**
 * Termos de liberação para menores — the síndico's queue, on the web.
 *
 * Each pending row is a child who is registered but cannot use the readers until somebody here
 * decides, which is why the panel opens on "Aguardando": everything else in it is a record, and
 * only the pending ones are a person waiting.
 *
 * A síndico is being asked to let a child through a door unaccompanied, so a row expands into the
 * WHOLE document — including the signature — rather than offering an Approve button next to a
 * summary. The PDF is the same document, for the physical file the condomínio already keeps.
 */

interface Props {
  customerId: string;
  customerName: string;
  /** Collapsed by default when the module is off for this customer. */
  enabled: boolean;
  /**
   * Narrows the panel to one unit — used on the unit pages, where the question is "which of THIS
   * apartment's children are authorised", not "what is waiting for me".
   *
   * Filtered client-side from the same request rather than by a new endpoint: the list is capped
   * at 200 and already carries unit_id, so a per-unit query would be a round trip and a backend
   * deploy to answer something the page already has in hand.
   */
  unitId?: string;
  /** On a unit page the panel is one section among many, so it opens on everything, not on the
   *  pending queue — a unit with nothing pending would otherwise look like it has no termos. */
  unitLabel?: string;
}

const FILTERS: { value: string; label: string }[] = [
  { value: 'pendente', label: 'Aguardando' },
  { value: 'aprovado', label: 'Aprovados' },
  { value: 'rejeitado', label: 'Rejeitados' },
];

export const MinorReleasesPanel: React.FC<Props> = ({
  customerId, customerName, enabled, unitId, unitLabel,
}) => {
  const scoped = !!unitId;
  const [open, setOpen] = useState(!scoped);
  // Scoped to a unit, "aprovado" is the useful default: the household's question is who IS
  // allowed in, not what is queued. The customer-wide panel opens on the queue.
  const [filter, setFilter] = useState(scoped ? 'aprovado' : 'pendente');
  const [all, setAll] = useState<MinorRelease[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!customerId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      // Everything in one request, filtered in memory, so the tabs can show counts for the ones
      // you are NOT on without three round trips.
      const resp = await listMinorReleases(customerId);
      const rows = resp.data || [];
      setAll(unitId ? rows.filter(r => r.unit_id === unitId) : rows);
    } catch (e) {
      setAll([]);
      setError(e instanceof Error ? e.message : 'Erro ao carregar os termos');
    } finally {
      setLoading(false);
    }
  }, [customerId, enabled, unitId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!enabled) return null;

  const rows = all.filter(r => r.status === filter);
  const countOf = (status: string) => all.filter(r => r.status === status).length;

  const decide = async (r: MinorRelease, approve: boolean) => {
    if (!approve && !rejectReason.trim()) {
      toast.error('Informe o motivo — o responsável vai lê-lo.');
      return;
    }
    setDeciding(r.id);
    try {
      await decideMinorRelease(customerId, r.id, approve, rejectReason.trim());
      toast.success(approve ? 'Termo aprovado. O acesso foi liberado.' : 'Termo rejeitado.');
      setRejectReason('');
      setExpanded(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não foi possível registrar a decisão');
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 text-gray-400 shrink-0" />
          <span className="text-sm font-semibold text-gray-800">Termos de Menores</span>
          <span className="text-xs text-gray-400">
            {unitLabel ? `— ${unitLabel}` : '— autorização dos responsáveis'}
          </span>
          {countOf('pendente') > 0 && (
            <span className="ml-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-600/20">
              {countOf('pendente')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {open && (
            <span
              role="button"
              tabIndex={0}
              onClick={e => { e.stopPropagation(); load(); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); load(); } }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="Recarregar"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-gray-100">
            {FILTERS.map(f => (
              <button
                key={f.value}
                type="button"
                onClick={() => { setFilter(f.value); setExpanded(null); }}
                className={cn(
                  'text-xs font-semibold px-3 py-1.5 rounded-full transition-colors',
                  filter === f.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {f.label} <span className="opacity-60">{countOf(f.value)}</span>
              </button>
            ))}
          </div>

          {loading && all.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <XCircle className="h-8 w-8 text-red-200" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-sm text-center text-gray-400">
              {scoped
                ? 'Nenhum termo para esta unidade.'
                : filter === 'pendente'
                  ? 'Nenhum termo aguardando decisão.'
                  : 'Nenhum termo aqui.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {rows.map(r => (
                <MinorReleaseRow
                  key={r.id}
                  release={r}
                  customerName={customerName}
                  expanded={expanded === r.id}
                  onToggle={() => { setExpanded(expanded === r.id ? null : r.id); setRejectReason(''); }}
                  deciding={deciding === r.id}
                  rejectReason={rejectReason}
                  onRejectReason={setRejectReason}
                  onDecide={approve => decide(r, approve)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MinorReleaseRow: React.FC<{
  release: MinorRelease;
  customerName: string;
  expanded: boolean;
  onToggle: () => void;
  deciding: boolean;
  rejectReason: string;
  onRejectReason: (v: string) => void;
  onDecide: (approve: boolean) => void;
}> = ({ release: r, customerName, expanded, onToggle, deciding, rejectReason, onRejectReason, onDecide }) => (
  <div className="px-5 py-3.5">
    <div className="flex items-start justify-between gap-4">
      <button type="button" onClick={onToggle} className="flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold text-gray-900">{r.minor_name || 'Menor sem nome'}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
          <span className="text-xs text-gray-500">{accessScopeLabel(r.access_scope)}</span>
          <span className="text-xs text-gray-500">Assinado por {r.signed_by_name || '—'}</span>
          <span className={cn(
            'text-[11px] font-semibold',
            r.status === 'pendente' ? 'text-amber-700'
              : r.status === 'aprovado' ? 'text-green-700' : 'text-red-600',
          )}>
            {minorReleaseStatusLabel(r.status)}
          </span>
        </div>
      </button>

      {/* Downloadable whatever the status — the watermark on an unapproved one is what keeps a
          printed copy honest, so there is no reason to withhold the file itself. */}
      <button
        type="button"
        onClick={() => downloadMinorReleasePdf(r, { customerName })}
        className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
        title="Baixar o termo em PDF"
      >
        <Download className="h-3.5 w-3.5" /> PDF
      </button>
    </div>

    {expanded && (
      <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
        <Field label="Responsável" value={`${r.guardian_name} · RG ${r.guardian_rg || '—'} · CPF ${r.guardian_cpf || '—'}`} />
        {r.father_name && <Field label="Pai" value={`${r.father_name} · RG ${r.father_rg || '—'} · CPF ${r.father_cpf || '—'}`} />}
        {r.mother_name && <Field label="Mãe" value={`${r.mother_name} · RG ${r.mother_rg || '—'} · CPF ${r.mother_cpf || '—'}`} />}
        <Field label="Menor" value={`${r.minor_name} · nasc. ${(r.minor_birth || '').split('-').reverse().join('/')}`} />
        <Field label="Autorização" value={accessScopeLabel(r.access_scope)} />

        {r.signature_data?.startsWith('data:image') && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Assinatura</p>
            <img src={r.signature_data} alt="Assinatura do responsável"
                 className="h-20 bg-white border border-gray-200 rounded-lg" />
          </div>
        )}

        {r.status === 'pendente' ? (
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              type="button"
              disabled={deciding}
              onClick={() => onDecide(true)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
            >
              Aprovar e liberar acesso
            </button>
            <input
              type="text"
              value={rejectReason}
              onChange={e => onRejectReason(e.target.value)}
              placeholder="Motivo da recusa"
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500/20"
            />
            <button
              type="button"
              disabled={deciding}
              onClick={() => onDecide(false)}
              className="px-4 py-2 text-sm font-semibold rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              Rejeitar
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-500">
            {minorReleaseStatusLabel(r.status)} por {r.decided_by_name || '—'}
            {r.decision_reason ? ` — ${r.decision_reason}` : ''}
          </p>
        )}
      </div>
    )}
  </div>
);

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-wrap gap-x-2">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 w-24 shrink-0 pt-0.5">
      {label}
    </span>
    <span className="text-sm text-gray-700">{value}</span>
  </div>
);
